import { ROUTE } from '@/core/api';
import { logSentryException } from '@/core/utils/sentry';
import { IncidentsUpdateMessage } from '@/server/services/PubSubService';

export type StreamMessage = IncidentsUpdateMessage & { publishedAt?: string };

export type MessageHandler = (message: IncidentsUpdateMessage) => void;

export class IncidentsStreamingEvents {
  private static instance: IncidentsStreamingEvents;

  private orgId: string;
  private es: EventSource | null = null;
  private connectedAt: number = 0;
  private handlers: Set<MessageHandler> = new Set();

  static getInstance(orgId: string) {
    if (!IncidentsStreamingEvents.instance) {
      IncidentsStreamingEvents.instance = new IncidentsStreamingEvents(orgId);
    }
    return IncidentsStreamingEvents.instance;
  }

  private constructor(orgId: string) {
    this.orgId = orgId;
  }

  private getEventSourceConstructor = async (): Promise<typeof EventSource> => {
    // Use native EventSource if present; otherwise load sse.js at runtime.
    if (typeof window === 'undefined') return EventSource as any;
    if ('EventSource' in window) return window.EventSource;
    return (await import('sse.js')).SSE as unknown as typeof EventSource;
  };

  async init() {
    this.connectedAt = Date.now();

    if (!this.es) {
      const EventSourceConstructor = await this.getEventSourceConstructor();
      this.es = new EventSourceConstructor(ROUTE.INCIDENTS.STREAM(this.orgId));

      this.es.addEventListener('open', () => {
        console.log('[IncidentsStreamingEvents] Connected');
      });

      this.es.addEventListener('error', (err) => {
        console.warn('[IncidentsStreamingEvents] Error:', err);
        logSentryException({
          message: 'IncidentsStreamingEvents error event',
          place: 'addEventListener',
          data: err,
        });
      });

      this.es?.addEventListener('message', (event) => {
        try {
          const message = JSON.parse(event.data) as StreamMessage;
          if (message.publishedAt) {
            const ts = Date.parse(message.publishedAt);
            if (!Number.isNaN(ts) && ts < this.connectedAt) return;
          }

          this.handlers.forEach((cb) => cb(message));
        } catch (err) {
          console.error('[IncidentsStreamingEvents] Failed to parse message:', err);
          logSentryException({
            message: 'IncidentsStreamingEvents message event',
            place: 'IncidentsStreamingEvents: Failed to parse message',
            data: err as Error,
          });
        }
      });
    }

    return this;
  }

  destroy() {
    this.es?.close();
    this.handlers.clear();
    this.es = null;
  }

  addEventListener = (...args: Parameters<EventSource['addEventListener']>) => {
    this.es?.addEventListener(...args);
  };

  onMessage = (handler: MessageHandler) => {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  };
}
